import clsx from "classnames";
import { HTMLMotionProps, motion } from "framer-motion";
import { HTMLProps } from "react";
import { Theme, DeprecatedTooltip } from "@landtechnologies/components";

export interface SideNavProps extends HTMLMotionProps<"div"> {
  expanded?: boolean;
}

export const SideNav = ({ children, className, expanded, ...rest }: SideNavProps) => {
  return (
    <motion.div
      data-testid="sidenav"
      transition={{
        type: "spring",
        damping: 40,
        stiffness: 700,
      }}
      layout
      {...rest}
      className={clsx(
        "atlas-flex atlas-flex-col atlas-grow atlas-p-1 atlas-bg-background-light atlas-drop-shadow-lg",
        expanded ? "atlas-w-40" : "atlas-w-12",
        className
      )}
    >
      {children}
    </motion.div>
  );
};

export type SideNavContentProps = HTMLProps<HTMLDivElement>;

export const SideNavContent = ({ children, className, ...rest }: SideNavContentProps) => {
  return (
    <div
      data-testid="sidenav-content"
      {...rest}
      className={clsx("atlas-flex atlas-flex-col atlas-flex-1", className)}
    >
      {children}
    </div>
  );
};

export type SideNavFooterProps = HTMLProps<HTMLDivElement>;

export const SideNavFooter = ({ children, className, ...rest }: SideNavFooterProps) => {
  return (
    <div
      data-testid="sidenav-footer"
      {...rest}
      className={clsx("atlas-flex atlas-flex-col", className)}
    >
      {children}
    </div>
  );
};
export interface SideNavItemProps extends HTMLMotionProps<"div"> {
  text: string;
  icon: string;
  active?: boolean;
  expanded?: boolean;
  dataTestID?: string;
}
const SideNavItem = ({
  text,
  icon,
  className,
  active,
  expanded,
  dataTestID,
  ...rest
}: SideNavItemProps) => {
  return (
    <DeprecatedTooltip
      theme={Theme.Dark}
      placement="right"
      description={text}
      yOffset={-50}
      disabled={expanded}
    >
      <motion.div
        data-testid={dataTestID ? `sidenav-item-${dataTestID}` : "sidenav-item"}
        whileTap={{ scale: 0.9 }}
        {...rest}
        className={clsx(
          "atlas-flex atlas-items-center atlas-rounded atlas-cursor-pointer  atlas-py-[5px] atlas-mb-1 atlas-text-base atlas-text-content-secondary",
          "hover:atlas-text-content-primary hover:atlas-bg-background-positive-subtle",
          expanded ? "" : "atlas-center atlas-justify-center",
          active
            ? "atlas-bg-background-positive-subtle !atlas-text-content-primary atlas-border atlas-border-border-action"
            : "",
          className
        )}
      >
        <i
          role="img"
          className={clsx(icon, expanded ? "atlas-px-2" : "atlas-px-1", "atlas-text-xl")}
        />
        {expanded && (
          <span
            data-testid={dataTestID}
            data-active={active ? "active" : ""}
            className={clsx(
              "atlas-text-sm atlas-pl-1",
              active ? "atlas-font-bold" : "atlas-font-semibold"
            )}
          >
            {text}
          </span>
        )}
      </motion.div>
    </DeprecatedTooltip>
  );
};

SideNav.Content = SideNavContent;
SideNav.Footer = SideNavFooter;
SideNav.Item = SideNavItem;
